import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { postSummaryPercent } from '@/api/summary'
import { useOffsetTop } from '@/hooks/useOffsetTop'
import { Summary, SummaryDetail } from '@/types/summary'
import { SchoolSummaryStatus } from '@/types/school'
import { CourseId, Reason, Srp, UserInfo } from '@/types/user'
import { getSrpStatus, isFacility } from '@/utils/user'
import {
  AuthorDetail,
  Body,
  Copyright,
  InputComment,
  Important,
  Notation,
  ReadMeter,
  Recommends,
  Review,
  MemberCommentList,
  ReadMore,
  ReadMoreSilver,
  SummaryLogin,
} from '.'
import { SchoolSurveyModal, useSchool } from './School'
import { useGetUserBookPercentQuery } from '@/stores'

interface Props {
  userInfo: UserInfo
  summary: SummaryDetail
  reason?: number
  readThisMonth?: number
  schoolStatus?: SchoolSummaryStatus
  recommends?: Summary[]
}

export const SummaryBody: React.FC<Props> = ({
  userInfo,
  summary,
  recommends = [],
  reason = Reason.CAN_NOT_READABLE,
  schoolStatus = null,
  readThisMonth = 0,
}) => {
  const router = useRouter()
  const finishReadRef = useRef(null)
  const [finish, setFinish] = useState(false)
  const { pageOffsetTop, viewportTop } = useOffsetTop(finishReadRef)
  const [lastMaxPercent, setLastMaxPercent] = useState(0)
  const { data: userBookPercents, refetch } = useGetUserBookPercentQuery()

  // スクロール量の調整。おすすめポイントまでの高さの半分からヘッダーの高さを引く
  const adjustHeight = isMobile ? 445 / 2 - 50 : 667 / 2 - 103
  const srpStatus = getSrpStatus(userInfo)

  // スクール向け
  const finishSchoolRef = useRef<HTMLDivElement>(null)
  const [schoolOpen, setSchoolOpen] = useSchool(schoolStatus, finishSchoolRef)

  // 初回表示用読了率
  const defaultBookPercent = () => {
    if (summary.percent > 0) {
      return summary.percent
    }
    return 0
  }

  /**
   * ユーザーの読了率を取得
   */
  const getUserBookPercent = (): number => {
    if (!userBookPercents) return 0
    if (userBookPercents.length > 0) {
      const bookPercent = userBookPercents.filter(
        (bookPercent) => bookPercent.summary_id === summary.id
      )
      if (bookPercent.length > 0) {
        return bookPercent[0].percent
      }
    }
    return 0
  }

  const percent = useMemo(() => {
    // 閲覧不可の場合
    if (reason != Reason.READABLE) return 0

    // 読了になったらそのまま
    if (finish) return 100

    // 位置を取得できなかった場合は読了率を0とする
    if (pageOffsetTop === undefined || viewportTop === undefined)
      return defaultBookPercent()

    // 現在の読了率
    const now = 100 - (viewportTop / (pageOffsetTop - adjustHeight)) * 100

    // 初期表示用
    if (lastMaxPercent < defaultBookPercent()) {
      setLastMaxPercent(defaultBookPercent())
    }

    // 下から上にスクロールしたときにパーセントが戻らないようにするため
    if (now < lastMaxPercent) {
      return lastMaxPercent
    }

    // DBの読了率を超えるまではDBの値を返す
    const userBookPercent = getUserBookPercent()
    if (userBookPercent && now < userBookPercent) {
      return userBookPercent
    }

    // 読了率の範囲は0~100%
    if (now <= 0) {
      return 0
    } else if (now >= 100) {
      setFinish(true)
      // 読了率を記録する
      postSummaryPercent(summary.id, 100)
      return 100
    } else {
      setLastMaxPercent(now)
      return now
    }
  }, [pageOffsetTop, viewportTop, userBookPercents])

  useEffect(() => {
    refetch()
    const userBookPercent = getUserBookPercent()
    setLastMaxPercent(userBookPercent)
    if (userBookPercent === 100) {
      setFinish(true)
      return
    }
    // 別の要約ページに遷移した場合は読了率を一旦初期化する
    setFinish(false)
  }, [summary.id, userBookPercents])

  /**
   * ページから離れる前に実行する処理
   */
  const beforeLeave = () => {
    postSummaryPercent(summary.id, Math.floor(percent))
  }

  useEffect(() => {
    router.events.on('routeChangeStart', beforeLeave)
    window.addEventListener('beforeunload', beforeLeave)
    // window.addEventListener('pagehide', beforeLeave)
    return () => {
      router.events.off('routeChangeStart', beforeLeave)
      window.removeEventListener('beforeunload', beforeLeave)
      // window.removeEventListener('pagehide', beforeLeave)
    }
  }, [percent, router.asPath])

  /**
   * reasonとコースIDによってもっと見るを出し分け
   */
  const renderReadMore = () => {
    const courseId = userInfo?.course?.id ?? CourseId.anonymous
    if (courseId === CourseId.anonymous) {
      return (
        <>
          <ReadMore
            link="/welcome?af_via=summary_b"
            borderText="この続きを見るには…"
            buttonSubText="3,000冊以上の要約が楽しめる"
            buttonText="会員登録（7日間無料）"
          />
          <SummaryLogin />
        </>
      )
    }
    if (reason !== Reason.READABLE) {
      if (courseId === CourseId.free) {
        return (
          <ReadMore
            link="/user/upgrade"
            borderText="この続きを読むにはアップグレードが必要です"
            buttonSubText="ゴールドプランなら読み放題"
            buttonText="続きを読む"
          />
        )
      } else if (courseId === CourseId.silver) {
        return (
          <ReadMoreSilver
            readThisMonth={readThisMonth}
            summaryId={summary.id}
          />
        )
      }
    } else {
      return (
        <>
          {/** 読了率100%の基準位置。現在はwww4のときと大体同じになるように配置している */}
          <div ref={finishReadRef} />
          <Notation title="一読のすすめ" body={summary.notation} />
          {/** スクールの振り返りモーダルを出すための、スクロール位置把握用の要素  */}
          {schoolStatus?.fromSchool && !schoolStatus?.isComplete && (
            <div ref={finishSchoolRef} />
          )}
        </>
      )
    }
  }

  return (
    <>
      <div className="mr-0 sm:mr-[30px] mb-[50px] w-full sm:w-[716px] h-auto sm:h-full text-[17px] align-top">
        <Review review={summary.review} writer={summary.writer} />
        <AuthorDetail title="著者" body={summary.about_author} />
        <Important important={summary.important} />
        <Body title="要約" body={summary.body} />
        {renderReadMore()}
        <Copyright text={summary.copyright} />
        {!isFacility(userInfo?.email) &&
          userInfo?.course?.id > CourseId.anonymous && (
            <>
              <InputComment summaryId={summary.id} srpStatus={srpStatus} />
              {(srpStatus === Srp.OPEN_SRP || srpStatus === Srp.CLOSE_SRP) && (
                <MemberCommentList summaryId={summary.id} />
              )}
            </>
          )}
        {recommends && <Recommends recommends={recommends} />}
      </div>
      {reason == Reason.READABLE && <ReadMeter percent={percent} />}
      {userInfo?.use_school && (
        <SchoolSurveyModal
          isOpen={schoolOpen}
          themeId={schoolStatus?.themeId}
          summaryId={schoolStatus?.schoolSummaryId}
          title={summary.title}
          onClose={() => setSchoolOpen(false)}
        />
      )}
    </>
  )
}
